/**
 * A frase de um pedido, em português — usada no push e no título do cartão.
 *
 * Uma função, testada uma vez: antes o push dizia sempre "A sessão quer usar
 * X" e o cartão remontava outra frase por conta própria.
 */

import { motorNaoTratado, type Motor } from '@remoto/protocolo';

import type { TipoPedido } from './index';

function corta(texto: string, teto = 80): string {
  const semQuebra = texto.replace(/\s+/g, ' ').trim();
  return semQuebra.length > teto ? semQuebra.slice(0, teto - 1) + '…' : semQuebra;
}

function textoDe(entrada: Record<string, unknown>, chave: string): string {
  const valor = entrada[chave];
  return typeof valor === 'string' ? valor : '';
}

/**
 * As frases do Codex, chaveadas pelo MÉTODO do `ServerRequest`.
 *
 * Não são nomes de ferramenta: no Codex o pedido não vem de uma ferramenta, vem
 * de uma requisição que o app-server faz ao cliente. Os cinco métodos e a forma
 * de cada carga estão medidos em `pedidos-codex.ts`, lidos dos bindings.
 *
 * A frase diz o que a pessoa precisa para DECIDIR, e nada além: quem quiser o
 * diff inteiro abre o cartão.
 */
function fraseDoCodex(metodo: string, entrada: Record<string, unknown>): string {
  if (metodo === 'item/commandExecution/requestApproval') {
    const comando = corta(textoDe(entrada, 'command'));
    // `writeStdin` NÃO é executar: é texto indo para um terminal já aberto.
    // Dizer "quer rodar" faria a pessoa aprovar uma coisa achando outra.
    if (entrada['kind'] === 'writeStdin') {
      return comando ? `A sessão quer digitar no terminal: ${comando}` : 'A sessão quer digitar no terminal aberto';
    }
    return comando ? `A sessão quer rodar: ${comando}` : 'A sessão quer rodar um comando';
  }

  if (metodo === 'item/fileChange/requestApproval') {
    const mudancas = entrada['changes'];
    if (Array.isArray(mudancas) && mudancas.length > 0) {
      const primeiro = corta(textoDe(mudancas[0] as Record<string, unknown>, 'path'));
      const resto = mudancas.length - 1;
      const sufixo = resto > 0 ? ` e mais ${resto}` : '';
      return primeiro ? `A sessão quer editar ${primeiro}${sufixo}` : 'A sessão quer editar arquivos';
    }
    // Sem `changes` é o caso normal, não defeito: o diff chega por outra
    // notificação e pode não ter chegado ainda.
    return 'A sessão quer editar arquivos';
  }

  if (metodo === 'item/tool/requestUserInput') {
    const perguntas = entrada['questions'];
    const primeira =
      Array.isArray(perguntas) && typeof (perguntas[0] as { question?: unknown })?.question === 'string'
        ? corta((perguntas[0] as { question: string }).question)
        : '';
    return primeira ? `A sessão pergunta: ${primeira}` : 'A sessão tem uma pergunta para você';
  }

  if (metodo === 'mcpServer/elicitation/request') {
    const servidor = corta(textoDe(entrada, 'serverName'), 40);
    /*
      A aprovação de ferramenta MCP chega disfarçada de elicitação (M0-9), com a
      pergunta em INGLÊS no `message`. Repeti-la mandaria "Allow the remoto MCP
      server to run tool…" para a notificação do telefone.
    */
    const meta = entrada['_meta'];
    if (
      typeof meta === 'object' &&
      meta !== null &&
      (meta as Record<string, unknown>)['codex_approval_kind'] === 'mcp_tool_call'
    ) {
      const nome = /"([^"]+)"/.exec(textoDe(entrada, 'message'))?.[1];
      return nome
        ? `Quer usar a ferramenta ${corta(nome, 60)} do servidor ${servidor || 'MCP'}`
        : `Quer usar uma ferramenta do servidor ${servidor || 'MCP'}`;
    }
    const recado = corta(textoDe(entrada, 'message'));
    if (recado) return `O servidor ${servidor || 'MCP'} pede: ${recado}`;
    return `O servidor ${servidor || 'MCP'} pede dados para continuar`;
  }

  if (metodo === 'item/permissions/requestApproval') {
    const perfil = entrada['permissions'];
    const pede: string[] = [];
    if (perfil !== null && typeof perfil === 'object') {
      const p = perfil as Record<string, unknown>;
      // `null` é o valor que o binding usa para "não peço nada desta área", e
      // ausente é o mesmo na prática. Os dois precisam contar como não-pedido.
      const pedido = (v: unknown): boolean => v !== null && v !== undefined;
      if (pedido(p['network'])) pede.push('rede');
      if (pedido(p['fileSystem'])) pede.push('disco');
    }
    // Perfil vazio É a negação, e não "pediu nada": dizer que pede permissão
    // sem dizer qual seria pedir um cheque em branco.
    return pede.length > 0
      ? `A sessão quer mais permissão de ${pede.join(' e ')}`
      : 'A sessão quer revisar as permissões da execução';
  }

  return `A sessão fez um pedido que este painel não conhece: ${corta(metodo, 60)}`;
}

/**
 * O primeiro padrão do pedido do OpenCode, que é o ALVO da permissão.
 *
 * No `permission.asked` (medido no OC-10) o alvo não mora num campo com nome
 * de ferramenta: vem em `patterns` — o arquivo no `edit`, o comando no `bash`,
 * o endereço no `webfetch`, a pasta no `external_directory`. `metadata` traz o
 * detalhe (`filepath`, `diff`), e é segunda opção só para o `edit`.
 */
function alvoDoOpencode(entrada: Record<string, unknown>): { primeiro: string; resto: number } {
  const padroes = entrada['patterns'];
  const lista = Array.isArray(padroes) ? padroes.filter((p): p is string => typeof p === 'string') : [];
  return { primeiro: corta(lista[0] ?? ''), resto: Math.max(0, lista.length - 1) };
}

/**
 * As frases do OpenCode, chaveadas pela `permission` do pedido.
 *
 * Não são nomes de ferramenta do Claude, e é por isso que moram à parte: a
 * `permission` do OpenCode é a CATEGORIA que a config dele governa (`edit`
 * cobre escrever, editar e aplicar patch; `bash` cobre todo comando), e um
 * `Read` do Claude casado por nome aqui descreveria outra coisa.
 *
 * `entrada` é o pedido como o daemon o recebeu: `patterns` e `metadata`. A
 * frase diz o que a pessoa precisa para DECIDIR; o diff inteiro fica no cartão.
 */
function fraseDoOpencode(permissao: string, entrada: Record<string, unknown>): string {
  const { primeiro, resto } = alvoDoOpencode(entrada);
  const mais = resto > 0 ? ` e mais ${resto}` : '';

  if (permissao === 'edit') {
    const meta = entrada['metadata'];
    const arquivo =
      primeiro ||
      (typeof meta === 'object' && meta !== null ? corta(textoDe(meta as Record<string, unknown>, 'filepath')) : '');
    return arquivo ? `A sessão quer editar ${arquivo}${mais}` : 'A sessão quer editar arquivos';
  }
  if (permissao === 'bash') {
    return primeiro ? `A sessão quer rodar: ${primeiro}${mais}` : 'A sessão quer rodar um comando';
  }
  if (permissao === 'webfetch') {
    return primeiro ? `A sessão quer acessar ${primeiro}` : 'A sessão quer acessar uma página';
  }
  if (permissao === 'websearch' || permissao === 'codesearch') {
    // A D26 liberou as duas buscas da Exa; o texto sai de casa, e a frase diz
    // que é busca na web, não leitura local.
    return primeiro ? `A sessão quer pesquisar na web: ${primeiro}` : 'A sessão quer pesquisar na web';
  }
  if (permissao === 'external_directory') {
    // A pasta FORA do projeto é o pedido que mais importa ler: é a sessão
    // saindo do `cwd` que o painel mostrou.
    return primeiro
      ? `A sessão quer mexer fora da pasta do projeto: ${primeiro}`
      : 'A sessão quer mexer fora da pasta do projeto';
  }
  if (permissao === 'task') {
    return primeiro ? `A sessão quer disparar um subagente: ${primeiro}` : 'A sessão quer disparar um subagente';
  }
  if (permissao === 'read' || permissao === 'list' || permissao === 'glob' || permissao === 'grep') {
    return primeiro ? `A sessão quer ler ${primeiro}` : 'A sessão quer ler arquivos';
  }
  if (permissao === 'question') {
    /*
      A PERGUNTA do OpenCode (`question.asked`, OC-33): o daemon a entrega com
      `ferramenta: 'question'` e as perguntas na forma do `AskUserQuestion`
      (`pedidos.ts`). Sem este caso, o título do cartão e o push diziam "A
      sessão quer usar question" — o nome cru da ferramenta no lugar da
      pergunta que a pessoa precisa ler para responder.

      Sem `questions` é o OUTRO pedido de mesmo nome: a permissão para USAR a
      ferramenta de pergunta (`permission.asked` com `permission: 'question'`).
      As regras da sessão a liberam (`modos.ts`), mas se um dia chegar, a
      frase não pode fingir que sabe o que vai ser perguntado.
    */
    const perguntas = entrada['questions'];
    if (!Array.isArray(perguntas)) return 'A sessão quer fazer uma pergunta';
    const primeira =
      typeof (perguntas[0] as { question?: unknown } | undefined)?.question === 'string'
        ? corta((perguntas[0] as { question: string }).question)
        : '';
    const mais = perguntas.length > 1 ? ` (e mais ${perguntas.length - 1})` : '';
    return primeira ? `A sessão pergunta: ${primeira}${mais}` : 'A sessão tem uma pergunta para você';
  }
  if (permissao === 'doom_loop') {
    // O OpenCode pede licença quando a sessão repete a mesma chamada: é o
    // motor dizendo que talvez esteja em laço, e a pessoa decide se segue.
    // (É uma chave da config dele; a forma deste pedido NÃO foi medida.)
    return 'A sessão repetiu a mesma ação várias vezes e pergunta se continua';
  }
  // Ferramenta MCP e o resto: o nome cru é melhor que uma frase inventada.
  return `A sessão quer usar ${corta(permissao, 60)}`;
}

export function fraseDoPedido(
  ferramenta: string,
  entrada: Record<string, unknown>,
  motor: Motor,
): string {
  switch (motor) {
    case 'CLAUDE':
      return fraseDoClaude(ferramenta, entrada);
    case 'CODEX':
      return fraseDoCodex(ferramenta, entrada);
    case 'OPENCODE':
      return fraseDoOpencode(ferramenta, entrada);
    default:
      return motorNaoTratado(motor);
  }
}

/** As frases do Claude, chaveadas pelo nome da ferramenta do CLI. */
function fraseDoClaude(ferramenta: string, entrada: Record<string, unknown>): string {
  if (ferramenta === 'Bash' || ferramenta === 'PowerShell') {
    const comando = corta(textoDe(entrada, 'command'));
    return comando ? `A sessão quer rodar: ${comando}` : 'A sessão quer rodar um comando';
  }

  const arquivo =
    textoDe(entrada, 'file_path') || textoDe(entrada, 'path') || textoDe(entrada, 'notebook_path');
  if (ferramenta === 'Write') {
    return arquivo ? `A sessão quer escrever ${corta(arquivo)}` : 'A sessão quer criar um arquivo';
  }
  if (ferramenta === 'Edit' || ferramenta === 'MultiEdit' || ferramenta === 'NotebookEdit') {
    return arquivo ? `A sessão quer editar ${corta(arquivo)}` : 'A sessão quer editar um arquivo';
  }
  if (ferramenta === 'Read') {
    return arquivo ? `A sessão quer ler ${corta(arquivo)}` : 'A sessão quer ler um arquivo';
  }

  if (ferramenta === 'AskUserQuestion') {
    const perguntas = entrada['questions'];
    const primeira =
      Array.isArray(perguntas) && typeof (perguntas[0] as { question?: unknown })?.question === 'string'
        ? corta((perguntas[0] as { question: string }).question)
        : '';
    return primeira ? `A sessão pergunta: ${primeira}` : 'A sessão tem uma pergunta para você';
  }
  if (ferramenta === 'ExitPlanMode') return 'A sessão terminou um plano e espera sua aprovação';
  if (ferramenta === 'EnterPlanMode') return 'A sessão quer voltar ao modo de planejamento';

  if (ferramenta === 'WebFetch') {
    const url = corta(textoDe(entrada, 'url'));
    return url ? `A sessão quer acessar ${url}` : 'A sessão quer acessar uma página';
  }
  if (ferramenta === 'WebSearch') {
    const busca = corta(textoDe(entrada, 'query'));
    return busca ? `A sessão quer pesquisar: ${busca}` : 'A sessão quer pesquisar na web';
  }

  if (ferramenta === 'Task' || ferramenta === 'Agent') {
    const descricao = corta(textoDe(entrada, 'description') || textoDe(entrada, 'prompt'));
    return descricao
      ? `A sessão quer disparar um subagente: ${descricao}`
      : 'A sessão quer disparar um subagente';
  }

  const mcp = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(ferramenta);
  if (mcp) return `A sessão quer usar ${mcp[2]} do servidor ${mcp[1]}`;

  return `A sessão quer usar ${ferramenta}`;
}

/**
 * O aviso que vai para o celular quando um pedido trava a sessão.
 *
 * Título e corpo saem do MESMO lugar porque erram juntos: o gateway montava o
 * título com um ternário e chamava a frase com o motor `'CLAUDE'` fixo, e um
 * pedido do Codex caía no genérico "A sessão quer usar
 * item/fileChange/requestApproval" — o push dizia o nome de um método JSON-RPC
 * em vez do arquivo que vai ser editado.
 *
 * **O título é do TIPO, não da ferramenta.** Quem lê a tela de bloqueio decide
 * em três palavras se vale desbloquear o telefone. E a ELICITACAO tem título
 * próprio: é o único pedido que não nasce da sessão, e sim de um servidor MCP
 * que ela consultou. "Permissão pendente" mandaria a pessoa procurar no cartão
 * uma permissão que não existe — o que se pede ali é um formulário.
 */
export function avisoDePedido(pedido: {
  tipo: TipoPedido;
  ferramenta: string;
  entrada: Record<string, unknown>;
  motor: Motor;
}): { titulo: string; corpo: string } {
  const titulo =
    pedido.tipo === 'PERGUNTA'
      ? 'A sessão tem uma pergunta'
      : pedido.tipo === 'PLANO'
        ? 'Plano esperando aprovação'
        : pedido.tipo === 'ELICITACAO'
          ? 'Um servidor pede dados'
          : 'Permissão pendente';
  return { titulo, corpo: fraseDoPedido(pedido.ferramenta, pedido.entrada, pedido.motor) };
}
